import React, { createContext, FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getUserDataWithUsername, IUserProps } from '../common/data/userDummyData';
import { ILoginResponse, ILoginResponseUser, ILoginToken } from '../Interface/ILoginResponse';

const AUTH_RESPONSE_KEY = 'gifc_authResponse';

/** Returns true if expiry string is in the past (with 30s buffer). */
export const isTokenExpired = (expiryStr: string | undefined): boolean => {
	if (!expiryStr) return true;
	try {
		const expiry = new Date(expiryStr).getTime();
		const now = Date.now();
		return expiry - 30 * 1000 <= now; // 30s buffer
	} catch {
		return true;
	}
};

const getStoredAuthResponse = (): ILoginResponse | null => {
	try {
		const raw = localStorage.getItem(AUTH_RESPONSE_KEY);
		if (!raw) return null;
		const data = JSON.parse(raw) as ILoginResponse;
		if (isTokenExpired(data?.token?.accessTokenExpiry)) {
			localStorage.removeItem(AUTH_RESPONSE_KEY);
			return null;
		}
		return data;
	} catch {
		return null;
	}
};

export interface IAuthContextProps {
	user: string;
	setUser?(...args: unknown[]): unknown;
	userData: Partial<IUserProps>;
	authData: ILoginResponse | null;
	setAuthData?(data: ILoginResponse | null): void;
	/** Convenience: token section from authData (or null if not logged in) */
	token: ILoginToken | null;
	/** Convenience: user section from authData (or null if not logged in) */
	authUser: ILoginResponseUser | null;
}
const AuthContext = createContext<IAuthContextProps>({} as IAuthContextProps);

interface IAuthContextProviderProps {
	children: ReactNode;
}
export const AuthContextProvider: FC<IAuthContextProviderProps> = ({ children }) => {
	const [user, setUser] = useState<string>(localStorage.getItem('gifc_authUsername') || '');
	const [userData, setUserData] = useState<Partial<IUserProps>>({});
	const [authData, setAuthDataState] = useState<ILoginResponse | null>(getStoredAuthResponse);
	const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const setAuthData = (data: ILoginResponse | null) => {
		setAuthDataState(data);
		if (data) {
			localStorage.setItem(AUTH_RESPONSE_KEY, JSON.stringify(data));
			const name = data.user?.email ?? data.user?.userName ?? '';
			if (name && typeof name === 'string') setUser(name);
		} else {
			localStorage.removeItem(AUTH_RESPONSE_KEY);
			setUser('');
		}
	};

	useEffect(() => {
		localStorage.setItem('gifc_authUsername', user);
	}, [user]);

	useEffect(() => {
		if (user !== '') {
			setUserData(getUserDataWithUsername(user));
		} else {
			setUserData({});
		}
	}, [user]);

	const value = useMemo(
		() => ({
			user,
			setUser,
			userData,
			authData,
			setAuthData,
			token: authData?.token ?? null,
			authUser: authData?.user ?? null,
		}),
		[user, userData, authData],
	);
	// Automatically clear authData when the access token expires,
	// so AuthGuard can redirect the user back to the login page.
	useEffect(() => {
		// Clear any existing timer
		if (logoutTimerRef.current) {
			clearTimeout(logoutTimerRef.current);
			logoutTimerRef.current = null;
		}

		const expiryStr = authData?.token?.accessTokenExpiry;
		if (!expiryStr) {
			return;
		}

		try {
			const expiryMs = new Date(expiryStr).getTime();
			const now = Date.now();
			// Use the same 30s safety buffer as isTokenExpired
			const bufferMs = 30 * 1000;
			const delay = expiryMs - now - bufferMs;

			if (delay <= 0) {
				setAuthData(null);
				return;
			}

			logoutTimerRef.current = setTimeout(() => {
				setAuthData(null);
			}, delay);
		} catch {
			setAuthData(null);
		}
	}, [authData?.token?.accessTokenExpiry]);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
AuthContextProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export default AuthContext;
